import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Consent-gated Google Analytics 4 loader.
 *
 * Behaviour:
 * - Reads REACT_APP_GA_MEASUREMENT_ID at build time. If missing → does nothing.
 * - Reads cookie-consent choice from localStorage. If choice !== "all" → does nothing.
 *   (Banner buttons map to: "necessary" = decline GA, "all" = accept GA.)
 * - When both conditions are met, injects gtag.js and configures the measurement ID.
 * - Re-fires gtag('event', 'page_view') on every client-side route change so React-Router
 *   navigations are tracked the same as initial page load.
 * - Reacts to consent changes in real time (other tab toggles or banner re-open).
 */
const STORAGE_KEY = "plainly.cookie-consent.v1";
const MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

function readConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw).choice;
  } catch {
    return null;
  }
}

function loadGtag() {
  if (window.__gaLoaded) return;
  if (!MEASUREMENT_ID) return;

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  window.gtag("config", MEASUREMENT_ID, {
    anonymize_ip: true,
    send_page_view: true,
  });
  window.__gaLoaded = true;
}

export default function GoogleAnalytics() {
  const location = useLocation();

  // Load (or not) on mount + when consent changes.
  useEffect(() => {
    if (!MEASUREMENT_ID) return;
    const tryLoad = () => {
      if (readConsent() === "all") loadGtag();
    };
    tryLoad();

    // Listen for consent changes in this tab (banner click) + other tabs (storage event).
    const onConsent = () => tryLoad();
    window.addEventListener("plainly:cookie-consent:changed", onConsent);
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) tryLoad();
    };
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("plainly:cookie-consent:changed", onConsent);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  // Track SPA route changes (only if GA has been loaded with consent).
  useEffect(() => {
    if (!MEASUREMENT_ID) return;
    if (!window.__gaLoaded) return;
    if (typeof window.gtag !== "function") return;
    window.gtag("event", "page_view", {
      page_path: location.pathname + location.search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location.pathname, location.search]);

  return null;
}
